import React, { useEffect, useRef } from "react";

// Shared setup helper — creates a fixed full-viewport canvas that respects
// devicePixelRatio and gets resized with the window.
const useCanvas2D = (draw) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  // Keep the draw function stable across renders — it's a setup callback
  // that only needs to run once. This avoids tearing down the animation on
  // every parent re-render.
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const reducedMotion =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const cleanup = drawRef.current(ctx, {
      startedAt: performance.now(),
      reducedMotion,
      setRaf: (id) => (rafRef.current = id),
    });

    return () => {
      window.removeEventListener("resize", resize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (typeof cleanup === "function") cleanup();
    };
  }, []);

  return canvasRef;
};

// ---------- Snow ----------
// Slowly drifting flakes with a small horizontal sway. Steady (no fade) —
// snow feels natural when it's just... always there.
export const SnowOverlay = () => {
  const canvasRef = useCanvas2D((ctx, { setRaf, reducedMotion }) => {
    const w = () => window.innerWidth;
    const h = () => window.innerHeight;
    const flakes = Array.from({ length: 90 }, () => ({
      x: Math.random() * w(),
      y: Math.random() * h(),
      r: 0.8 + Math.random() * 2.6,
      vy: 0.4 + Math.random() * 1.6,
      swayAmp: 8 + Math.random() * 22,
      swayFreq: 0.0005 + Math.random() * 0.0012,
      swayPhase: Math.random() * Math.PI * 2,
      opacity: 0.35 + Math.random() * 0.45,
    }));

    if (reducedMotion) {
      // Static snapshot only.
      ctx.fillStyle = "rgba(255,255,255,0.6)";
      flakes.forEach((f) => {
        ctx.globalAlpha = f.opacity;
        ctx.beginPath();
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      return;
    }

    const tick = (now) => {
      const W = w();
      const H = h();
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "rgba(255,255,255,1)";
      for (const f of flakes) {
        f.y += f.vy;
        if (f.y - f.r > H) {
          f.x = Math.random() * W;
          f.y = -f.r - Math.random() * 40;
        }
        const sway = Math.sin(now * f.swayFreq + f.swayPhase) * f.swayAmp;
        ctx.globalAlpha = f.opacity;
        ctx.beginPath();
        ctx.arc(f.x + sway, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      setRaf(requestAnimationFrame(tick));
    };
    setRaf(requestAnimationFrame(tick));
  });

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[1] pointer-events-none"
      aria-hidden="true"
      data-testid="snow-overlay"
    />
  );
};

// ---------- Stars ----------
// A field of gently twinkling stars, plus occasional soft shooting stars.
export const StarsOverlay = () => {
  const canvasRef = useCanvas2D((ctx, { setRaf, reducedMotion }) => {
    const W0 = window.innerWidth;
    const H0 = window.innerHeight;
    const stars = Array.from({ length: 130 }, () => ({
      x: Math.random() * W0,
      y: Math.random() * H0 * 0.75, // concentrate near the sky
      r: 0.4 + Math.random() * 1.4,
      base: 0.15 + Math.random() * 0.45,
      amp: 0.15 + Math.random() * 0.35,
      freq: 0.0006 + Math.random() * 0.0022,
      phase: Math.random() * Math.PI * 2,
    }));

    // Shooting star state — only ever one active at a time.
    let shooter = null;
    let nextShooterAt = performance.now() + 6000 + Math.random() * 12000;

    if (reducedMotion) {
      ctx.fillStyle = "rgba(255,255,255,1)";
      stars.forEach((s) => {
        ctx.globalAlpha = s.base;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      return;
    }

    const spawnShooter = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      const fromLeft = Math.random() < 0.5;
      const y0 = Math.random() * H * 0.4;
      shooter = {
        x: fromLeft ? -80 : W + 80,
        y: y0,
        vx: (fromLeft ? 1 : -1) * (12 + Math.random() * 8),
        vy: 4 + Math.random() * 3,
        life: 0,
        maxLife: 90 + Math.random() * 60, // frames
      };
    };

    const tick = (now) => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);
      ctx.fillStyle = "rgba(255,255,255,1)";
      for (const s of stars) {
        const twinkle =
          s.base + Math.sin(now * s.freq + s.phase) * s.amp * 0.5;
        ctx.globalAlpha = Math.max(0.05, twinkle);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (!shooter && now >= nextShooterAt) {
        spawnShooter();
      }
      if (shooter) {
        shooter.life += 1;
        shooter.x += shooter.vx;
        shooter.y += shooter.vy;
        const t = shooter.life / shooter.maxLife;
        const alpha = Math.sin(Math.min(t, 1) * Math.PI) * 0.85; // fade in and out
        // Draw as a short bright streak.
        ctx.strokeStyle = "rgba(255,255,255,1)";
        ctx.globalAlpha = alpha;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(shooter.x, shooter.y);
        ctx.lineTo(shooter.x - shooter.vx * 3, shooter.y - shooter.vy * 3);
        ctx.stroke();
        ctx.globalAlpha = 1;
        if (
          shooter.life > shooter.maxLife ||
          shooter.x < -200 ||
          shooter.x > W + 200 ||
          shooter.y > H + 200
        ) {
          shooter = null;
          nextShooterAt = now + 8000 + Math.random() * 16000;
        }
      }

      setRaf(requestAnimationFrame(tick));
    };
    setRaf(requestAnimationFrame(tick));
  });

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[1] pointer-events-none"
      aria-hidden="true"
      data-testid="stars-overlay"
    />
  );
};

// ---------- Mist (used for ocean shore) ----------
// Wide, low-opacity horizontal fog bands that drift across the screen at
// different speeds. Evokes distant sea spray without covering the horizon.
export const MistOverlay = () => {
  const canvasRef = useCanvas2D((ctx, { setRaf, reducedMotion }) => {
    const bands = Array.from({ length: 6 }, (_, i) => ({
      x: Math.random() * window.innerWidth,
      // Concentrate bands in the middle band of the screen so the horizon
      // stays legible.
      y: window.innerHeight * (0.35 + i * 0.08 + Math.random() * 0.04),
      w: 400 + Math.random() * 500,
      h: 60 + Math.random() * 80,
      vx: 0.12 + Math.random() * 0.35,
      opacity: 0.06 + Math.random() * 0.09,
    }));

    if (reducedMotion) {
      bands.forEach((b) => {
        const grad = ctx.createRadialGradient(
          b.x, b.y, 0, b.x, b.y, Math.max(b.w, b.h) / 2,
        );
        grad.addColorStop(0, `rgba(220,230,235,${b.opacity})`);
        grad.addColorStop(1, "rgba(220,230,235,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(b.x - b.w / 2, b.y - b.h / 2, b.w, b.h);
      });
      return;
    }

    const tick = () => {
      const W = window.innerWidth;
      const H = window.innerHeight;
      ctx.clearRect(0, 0, W, H);
      for (const b of bands) {
        b.x += b.vx;
        if (b.x - b.w / 2 > W) b.x = -b.w / 2;
        const grad = ctx.createRadialGradient(
          b.x, b.y, 0, b.x, b.y, Math.max(b.w, b.h) / 2,
        );
        grad.addColorStop(0, `rgba(220,230,235,${b.opacity})`);
        grad.addColorStop(1, "rgba(220,230,235,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(b.x - b.w / 2, b.y - b.h / 2, b.w, b.h);
      }
      setRaf(requestAnimationFrame(tick));
    };
    setRaf(requestAnimationFrame(tick));
    // Suppress unused var lint since we don't use H directly.
    void 0;
  });

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-[1] pointer-events-none"
      aria-hidden="true"
      data-testid="mist-overlay"
    />
  );
};
